'use client';

import { Pause, Play, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

type Phase = 'focus' | 'short-break' | 'long-break';

const DURATIONS: Record<Phase, number> = {
  focus: 25 * 60,
  'short-break': 5 * 60,
  'long-break': 15 * 60,
};

const PHASE_LABEL: Record<Phase, string> = {
  focus: 'Focus',
  'short-break': 'Short break',
  'long-break': 'Long break',
};

function nextPhase(phase: Phase, completedFocusSessions: number): Phase {
  if (phase !== 'focus') return 'focus';
  return (completedFocusSessions + 1) % 4 === 0 ? 'long-break' : 'short-break';
}

export function PomodoroTimer() {
  const [phase, setPhase] = useState<Phase>('focus');
  const [secondsLeft, setSecondsLeft] = useState(DURATIONS.focus);
  const [running, setRunning] = useState(false);
  const [sessions, setSessions] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          setRunning(false);
          setSessions((prevSessions) => {
            const wasFocus = phase === 'focus';
            const updated = wasFocus ? prevSessions + 1 : prevSessions;
            const next = nextPhase(phase, prevSessions);
            setPhase(next);
            setSecondsLeft(DURATIONS[next]);
            return updated;
          });
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [running, phase]);

  function reset() {
    setRunning(false);
    setPhase('focus');
    setSecondsLeft(DURATIONS.focus);
  }

  function switchPhase(next: Phase) {
    setRunning(false);
    setPhase(next);
    setSecondsLeft(DURATIONS[next]);
  }

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const progress = 1 - secondsLeft / DURATIONS[phase];

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <div className="flex justify-center gap-2">
        {(['focus', 'short-break', 'long-break'] as Phase[]).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => switchPhase(p)}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
              phase === p
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border text-muted-foreground hover:bg-muted'
            }`}
          >
            {PHASE_LABEL[p]}
          </button>
        ))}
      </div>

      <div className="relative mx-auto mt-6 flex size-48 items-center justify-center">
        <svg viewBox="0 0 100 100" className="absolute inset-0 -rotate-90" aria-hidden="true">
          <circle cx="50" cy="50" r="45" fill="none" stroke="var(--border)" strokeWidth="6" />
          <circle
            cx="50"
            cy="50"
            r="45"
            fill="none"
            stroke="var(--primary)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={2 * Math.PI * 45}
            strokeDashoffset={2 * Math.PI * 45 * (1 - progress)}
            className="transition-[stroke-dashoffset] duration-1000"
          />
        </svg>
        <span className="font-heading text-4xl font-bold tabular-nums text-foreground">
          {minutes}:{seconds.toString().padStart(2, '0')}
        </span>
      </div>

      <div className="mt-6 flex justify-center gap-3">
        <button
          type="button"
          onClick={() => setRunning((r) => !r)}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          {running ? <Pause className="size-4" /> : <Play className="size-4" />}
          {running ? 'Pause' : 'Start'}
        </button>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-2 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted"
        >
          <RotateCcw className="size-4" /> Reset
        </button>
      </div>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        {sessions} focus session{sessions === 1 ? '' : 's'} completed
      </p>
    </div>
  );
}
